import type Quill from "quill";
import "quill/dist/quill.snow.css";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import ModalComp from "./ModalComp";

export type QuillEditorRef = {
    setContents: (html: string) => void;
};

type QuillEditorProps = {
    initialContent: string;
    onChange: (html: string) => void;
    disabled?: boolean
    showPreview?: boolean
    onQuillParseFail?: () => void
};

const QuillEditor = forwardRef<QuillEditorRef, QuillEditorProps>(
    ({ initialContent, onChange, disabled, showPreview, onQuillParseFail }, ref) => {
        const preview = showPreview ?? true;

        const editorRef = useRef<HTMLDivElement>(null);
        const quillInstanceRef = useRef<Quill | null>(null);
        const [modalOpen, setModalOpen] = useState<boolean>(false);

        useImperativeHandle(ref, () => ({
            setContents: (html: string) => {
                if (quillInstanceRef.current) {
                    quillInstanceRef.current.clipboard.dangerouslyPasteHTML(html);
                }
            },
        }));

        useEffect(() => {
            const loadQuill = async () => {
                const Quill = (await import("quill")).default;

                if (editorRef.current && !quillInstanceRef.current) {
                    quillInstanceRef.current = new Quill(editorRef.current, {
                        theme: "snow",
                        modules: {
                            toolbar: [
                                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                [{ font: [] }],
                                ["bold", "italic", "underline", "strike"],
                                [{ color: [] }, { background: [] }],
                                [{ script: "sub" }, { script: "super" }], // subscript/superscript
                                [{ list: "ordered" }, { list: "bullet" }],
                            ],
                        },
                    });


                    // console.log({ initialContent });

                    if (initialContent) {
                        quillInstanceRef.current.root.innerHTML = initialContent;
                        if (onQuillParseFail !== undefined) {
                            const delta = quillInstanceRef.current.clipboard.convert({ html: initialContent });
                            if (!delta?.ops?.length) {
                                onQuillParseFail();
                            }
                        }
                    }

                    quillInstanceRef.current.on("text-change", () => {
                        const html = quillInstanceRef.current?.root.innerHTML;
                        onChange?.(html ?? '');
                    });
                }
            };

            if (typeof window !== "undefined") {
                loadQuill();
            }
        }, [initialContent, onChange]);

        useEffect(() => {
            if (quillInstanceRef.current) {
                quillInstanceRef.current.enable(!!!disabled);
            }
        }, [disabled]);

        return (
            <>
                <div className="rounded-xl my-2 h-[350px] md:h-[320px] lg:h-[290px] bg-slate-50 dark:bg-slate-950">
                    <div ref={editorRef} style={{ height: "250px" }} />
                </div>
                {preview && quillInstanceRef.current?.root.innerHTML &&
                    <button className="btn btn-outline-success btn-sm" type="button" onClick={() => setModalOpen(true)}>
                        Preview
                    </button>
                }
                {preview &&
                    <ModalComp
                        closeBtnView
                        onClose={() => setModalOpen(false)}
                        open={modalOpen}
                        sizeClass="md:w-[50%] w-[80%]"
                        title="Preview"
                        zIndex={9999}
                    >
                        {/* <section className='custom-scroll max-h-[80vh] lg:max-h-[60vh]' dangerouslySetInnerHTML={{ __html: quillInstanceRef.current?.root.innerHTML ?? '' }} /> */}
                        <iframe
                            srcDoc={quillInstanceRef.current?.root.innerHTML ?? ""}
                            className="w-full border-none custom-scroll max-h-[80vh] lg:max-h-[60vh] "
                            sandbox="allow-same-origin"
                            title="Preview"
                        />
                    </ModalComp>
                }
            </>
        );
    }
);

QuillEditor.displayName = "QuillEditor";

export default QuillEditor;

